import type { StoreApi } from 'zustand';
import { demoLeadsApi } from '../api/demoLeadsApi';
import { getAdminApiError } from '../lib/adminApiErrors';
import { getDemoConversionConflict, mustClearSensitiveAdminState } from '../lib/legalAdminErrors';
import { createAdminIdempotencyKey } from '../lib/adminIdempotency';
import type { AdminDemoLeadConversionInput, AdminDemoLeadPage } from '../legalTypes';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

type AdminStore = SuperAdminState & SuperAdminActions;
type SetAdminStore = StoreApi<AdminStore>['setState'];
type DemoLeadActions = Pick<
  SuperAdminActions,
  | 'fetchDemoLeads'
  | 'loadDemoLead'
  | 'clearDemoLead'
  | 'updateDemoLeadStatus'
  | 'convertDemoLead'
  | 'loadDemoLeadConversionJob'
  | 'clearDemoLeadConversion'
>;

export const EmptyDemoLeadPage: AdminDemoLeadPage = {
  items: [],
  page: 1,
  pageSize: 25,
  totalCount: 0,
};

const conversionFingerprint = (leadId: number, input: AdminDemoLeadConversionInput): string =>
  `${leadId}:${JSON.stringify(input)}`;

interface DemoLeadRuntime {
  detailGeneration: number;
  retryIdentity: { fingerprint: string; key: string } | null;
  listController: AbortController | null;
}

const clearSensitiveLeadState = (set: SetAdminStore, runtime: DemoLeadRuntime) => {
  runtime.detailGeneration += 1;
  runtime.listController?.abort();
  runtime.listController = null;
  runtime.retryIdentity = null;
  set({
    demoLeads: EmptyDemoLeadPage,
    demoLeadDetail: null,
    demoLeadConversion: null,
    demoLeadConversionConflict: null,
    demoLeadConversionJob: null,
  });
};

const createDemoLeadReadActions = (
  set: SetAdminStore,
  runtime: DemoLeadRuntime
): Pick<DemoLeadActions, 'fetchDemoLeads' | 'loadDemoLead' | 'clearDemoLead'> => {
  const fetchDemoLeads: DemoLeadActions['fetchDemoLeads'] = async (query) => {
    runtime.listController?.abort();
    const controller = new AbortController();
    runtime.listController = controller;
    set({ demoLeadLoading: true, demoLeadError: null });
    try {
      const result = await demoLeadsApi.list(query, controller.signal);
      if (runtime.listController !== controller) {
        return result;
      }
      const needle = query.q?.trim().toLocaleLowerCase();
      const items = needle
        ? result.items.filter((lead) => [
          lead.companyName, lead.firstName, lead.lastName, lead.email, lead.country,
        ].some((value) => value.toLocaleLowerCase().includes(needle)))
        : result.items;
      const page = items === result.items
        ? result
        : { ...result, items, totalCount: items.length };
      set({ demoLeads: page, demoLeadLoading: false });
      return page;
    } catch (error) {
      if (runtime.listController !== controller) {
        throw error;
      }
      const failure = getAdminApiError(error, 'demo_leads_failed');
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveLeadState(set, runtime);
      }
      set({ demoLeadError: failure, demoLeadLoading: false });
      throw error;
    }
  };

  const loadDemoLead: DemoLeadActions['loadDemoLead'] = async (leadId) => {
    const generation = ++runtime.detailGeneration;
    set({ demoLeadDetail: null, demoLeadDetailLoading: true, demoLeadError: null });
    try {
      const lead = await demoLeadsApi.get(leadId);
      if (generation === runtime.detailGeneration) {
        set({ demoLeadDetail: lead, demoLeadDetailLoading: false });
      }
      return lead;
    } catch (error) {
      if (generation === runtime.detailGeneration) {
        if (mustClearSensitiveAdminState(error)) {
          clearSensitiveLeadState(set, runtime);
        }
        set({
          demoLeadDetail: null,
          demoLeadDetailLoading: false,
          demoLeadError: getAdminApiError(error, 'demo_lead_detail_failed'),
        });
      }
      throw error;
    }
  };

  const clearDemoLead = () => {
    runtime.detailGeneration += 1;
    set({ demoLeadDetail: null, demoLeadDetailLoading: false, demoLeadError: null });
  };
  return { fetchDemoLeads, loadDemoLead, clearDemoLead };
};

const createDemoLeadMutationActions = (
  set: SetAdminStore,
  runtime: DemoLeadRuntime
): Pick<
  DemoLeadActions,
  'updateDemoLeadStatus' | 'convertDemoLead' | 'loadDemoLeadConversionJob' | 'clearDemoLeadConversion'
> => {
  const updateDemoLeadStatus: DemoLeadActions['updateDemoLeadStatus'] = async (leadId, request) => {
    set({ demoLeadMutating: true, demoLeadError: null });
    try {
      const lead = await demoLeadsApi.updateStatus(leadId, request);
      set((state) => ({
        demoLeadDetail: lead,
        demoLeadMutating: false,
        demoLeads: {
          ...state.demoLeads,
          items: state.demoLeads.items.map((item) => item.id === lead.id ? lead : item),
        },
      }));
      return lead;
    } catch (error) {
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveLeadState(set, runtime);
      }
      set({
        demoLeadMutating: false,
        demoLeadError: getAdminApiError(error, 'demo_lead_status_failed'),
      });
      throw error;
    }
  };

  const convertDemoLead: DemoLeadActions['convertDemoLead'] = async (leadId, input) => {
    const fingerprint = conversionFingerprint(leadId, input);
    if (runtime.retryIdentity?.fingerprint !== fingerprint) {
      runtime.retryIdentity = { fingerprint, key: createAdminIdempotencyKey() };
    }
    const key = runtime.retryIdentity.key;
    set({ demoLeadConverting: true, demoLeadError: null, demoLeadConversionConflict: null });
    try {
      const result = await demoLeadsApi.convert(leadId, input, key);
      set((state) => ({
        demoLeadConverting: false,
        demoLeadConversion: result,
        demoLeadDetail: state.demoLeadDetail?.id === leadId
          ? { ...state.demoLeadDetail, status: 'converted', convertedCompanyId: result.companyId }
          : state.demoLeadDetail,
        demoLeads: {
          ...state.demoLeads,
          items: state.demoLeads.items.map((lead) => lead.id === leadId
            ? { ...lead, status: 'converted' }
            : lead),
        },
      }));
      return result;
    } catch (error) {
      const failure = getAdminApiError(error, 'demo_lead_conversion_failed');
      const existing = getDemoConversionConflict(error);
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveLeadState(set, runtime);
      }
      set({
        demoLeadConverting: false,
        demoLeadError: failure,
        demoLeadConversionConflict: existing ? { error: failure, existing } : null,
      });
      throw error;
    }
  };

  const loadDemoLeadConversionJob: DemoLeadActions['loadDemoLeadConversionJob'] = async (leadId) => {
    try {
      const job = await demoLeadsApi.getConversionJob(leadId);
      set({ demoLeadConversionJob: job });
      return job;
    } catch (error) {
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveLeadState(set, runtime);
      }
      set({ demoLeadError: getAdminApiError(error, 'demo_conversion_job_failed') });
      throw error;
    }
  };

  const clearDemoLeadConversion = () => {
    runtime.retryIdentity = null;
    set({
      demoLeadConversion: null,
      demoLeadConversionConflict: null,
      demoLeadConversionJob: null,
    });
  };
  return {
    updateDemoLeadStatus,
    convertDemoLead,
    loadDemoLeadConversionJob,
    clearDemoLeadConversion,
  };
};

export const createDemoLeadStoreActions = (set: SetAdminStore): DemoLeadActions => {
  const runtime: DemoLeadRuntime = {
    detailGeneration: 0,
    retryIdentity: null,
    listController: null,
  };
  return {
    ...createDemoLeadReadActions(set, runtime),
    ...createDemoLeadMutationActions(set, runtime),
  };
};
